import "../../../App.css";
import log from "loglevel";
import GoogleMapReact from "google-map-react";
import {FunctionComponent, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Constants, OptimizedResultCords, UserPreferences} from "../../../constants/constants";
import ManifestDetailsMapOverlay from "./ManifestDetailsMapOverlay";
import {DriverLocation, Manifest, SearchDriverLocationQuery, useSetUserPrefMutation} from "../../../generated/graphql";
import StopMarker from "../stop/StopMarker";
import {useGeoCodeService} from "../../../hooks/useGeoCodeService";
import {getRouted, getRoutedEntities, useMapVisibilityDispatch, useMapVisibilityState} from "../MapVisibilityContext";
import DriverMarker from "../driver/DriverMarker";
import {Cords, createSinglePolyline, getMapBounds, getRoutePaths, MapPath} from "../MappingService";
import {TOptimizedInfo} from "../../manifest/details/ManifestDetailsV2";
import {getDriverCords, getStopLatLng, MappedStop} from "../AssignmentMapV2";
import useMapRoutes, {RoutePath} from "../useMapRoutes";
import _ from "lodash";
import {Icon} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {mapCompare} from "../../../utils/General";
import {QueryResult} from "@apollo/client";
import {ManifestDetailActionBar} from "./ManifestDetailActionbar";
import {createJsonPref, extractJsonPref, PreferenceContext} from "../../../providers/PreferenceProvider";
import {isCompleted} from "../../../utils/stops/StopUtils";
import {TManifestStopEnhanced} from "../../common/types";

export enum RouteViewMode {
  Optimized = "optimized",
  Original = "original"
}

export type ManifestDetailsMapViewProps = {
  manifest: Manifest | undefined;
  optimizedStops?: TManifestStopEnhanced[];
  driverLocationResult: QueryResult<SearchDriverLocationQuery>;
  isDraggedStops?: boolean;
  mapJobStop?: number[];
  isOptimized: boolean;
  optimizedInfo?: TOptimizedInfo;
  optimizedResultCords: Map<OptimizedResultCords, {lat: number; lng: number}[]> | null;
};

interface MapActionBarPrefs {
  isActiveCompletedStops: boolean;
}

const defaultMapActionBarPrefs = {
  isActiveCompletedStops: false
};

/*
 * DP-3180: publishing a new `latLngStops` identity cascades into the route-path effect ->
 * setRoutes -> useMapRoutes (which has no dedup) -> a fresh GraphHopper query -> new routeInfo
 * -> the fitBounds effect -> map.fitBounds(), snapping the camera back under the dispatcher.
 * The 15s grid tick (ManifestDetailsV2 processGridUpdates -> setWorkingStops) rebuilds the stop
 * objects every time, so the guard below keeps the previous reference unless something the map
 * actually draws has changed.
 *
 * The map never draws these fields: they are recomputed server-side (the DP-2999 indicator
 * columns) or client-side (ETA, applied to optimized stops), so comparing them would defeat the
 * guard on every refresh. If you make the map render one of them, drop it from this list.
 */
const UNDRAWN_STOP_FIELDS = [
  "eta",
  "etaSource",
  "generatedDateTime",
  "indicatorRank",
  "indicatorColor",
  "indicatorExpiry",
  "indicatorSnoozeUntil"
];

/* the part of the geocoded stop map that determines what the map draws */
const drawnStops = (byLatLng: Record<string, MappedStop[]>) =>
  _.mapValues(byLatLng, (stops) => stops.map((stop) => _.omit(stop, UNDRAWN_STOP_FIELDS)));

/*
 * Key insertion order is load-bearing and _.isEqual ignores it, so compare it separately:
 * getRoutePaths is called with isSortWithSequence=false, so it walks Object.keys() order when it
 * builds the polyline, and the route cache key joins the keys unsorted. Re-ordering stops that
 * kept their addresses has to invalidate.
 */
const sameDrawnStops = (a: Record<string, MappedStop[]>, b: Record<string, MappedStop[]>) =>
  Object.keys(a).join("|") === Object.keys(b).join("|") && _.isEqual(drawnStops(a), drawnStops(b));

const ManifestDetailsMap: FunctionComponent<ManifestDetailsMapViewProps> = ({
  manifest,
  driverLocationResult,
  optimizedStops,
  isDraggedStops,
  mapJobStop,
  isOptimized,
  optimizedInfo,
  optimizedResultCords
}) => {
  const geoCodeService = useGeoCodeService();
  const [apiLoaded, setApiLoaded] = useState<boolean>(false);
  const [map, setMap] = useState<any>();
  const [maps, setMaps] = useState<any>();
  const mapVisibilityState = useMapVisibilityState();
  const mapVisibilityDispatch = useMapVisibilityDispatch();

  const [latLngStops, setLatLngStops] = useState<Record<string /* lat,lng */, MappedStop[]>>({});
  const [driverLocations, setDriverLocations] = useState<Map<number, DriverLocation>>(new Map());
  const [, setRoutePathLine] = useState<MapPath | null>(null);
  const [routeInfo, setRoutes] = useMapRoutes();
  const [viewMode, setViewMode] = useState<RouteViewMode>(RouteViewMode.Original);
  const {userPreferences, userPrefsQueryRefetch, isUserPreferencesLoaded} = useContext(PreferenceContext);
  const [setUserPref] = useSetUserPrefMutation();
  const [initialManifestMap, setInitialManifestMap] = useState<boolean>(true);
  const [apiKey] = useState({key: Constants.GOOGLE_API_KEY});
  const [defaultCenter] = useState({lat: 37.0902, lng: -95.7129});
  const [userGestureActive, setUserGestureActive] = useState(false);
  const [isActiveCompletedStops, setIsActiveCompletedStops] = useState(defaultMapActionBarPrefs.isActiveCompletedStops);

  const mapActionBarPrefs = useRef<MapActionBarPrefs>(defaultMapActionBarPrefs);

  const updateMapActionBarPrefs = useCallback(() => {
    setUserPref({
      variables: {
        name: UserPreferences.manifestMapActionBar,
        input: createJsonPref(mapActionBarPrefs.current, true)
      }
    }).then(() => {
      userPrefsQueryRefetch?.();
    });
  }, [setUserPref, userPrefsQueryRefetch]);

  const onToggleActiveCompletedStops = useCallback(() => {
    setIsActiveCompletedStops((prevState) => {
      mapActionBarPrefs.current.isActiveCompletedStops = !prevState;
      updateMapActionBarPrefs();
      return !prevState;
    });
  }, [updateMapActionBarPrefs]);

  useEffect(() => {
    if (initialManifestMap && isUserPreferencesLoaded) {
      const mapActionBarPrefs = extractJsonPref(userPreferences, UserPreferences.manifestMapActionBar)?.value;
      if (mapActionBarPrefs) {
        setIsActiveCompletedStops(mapActionBarPrefs.isActiveCompletedStops);
      }
      setInitialManifestMap(false);
    }
  }, [initialManifestMap, userPreferences, isUserPreferencesLoaded]);

  const handleUserGestureActive = useCallback(() => {
    setUserGestureActive(true);
  }, []);

  useEffect(() => {
    if (isOptimized) {
      setViewMode(RouteViewMode.Optimized);
    } else {
      setViewMode(RouteViewMode.Original);
    }
  }, [isOptimized]);

  useEffect(() => {
    /*
     * Convert visible jobs and manifests into a KV map. Keys is lat/lng, values
     * are an array of stopped at key's location
     */
    let stillMounted = true;
    (async () => {
      const routedVisibleManifests = getRoutedEntities(mapVisibilityState.manifests);
      const visibleManifestStops: MappedStop[] =
        routedVisibleManifests.length === 0
          ? []
          : routedVisibleManifests.flatMap(({entityId, visibilityLevel}) => {
              if (!manifest) {
                return [];
              }
              if (!manifest.stops) return [];
              const stops = (
                (viewMode === RouteViewMode.Optimized || isDraggedStops) && optimizedStops
                  ? optimizedStops
                  : manifest.stops
              ) as TManifestStopEnhanced[];
              return stops.flatMap((stop, i): MappedStop[] => {
                if ((!isActiveCompletedStops || isOptimized) && isCompleted(stop)) return [];
                return [
                  {
                    ...stop,
                    stopInfoMetaData: {
                      key: `Manifest_${entityId}`,
                      customer: stop.order.customer.name!,
                      jobNumber: stop.job.jobNumber,
                      service: stop.order.service!,
                      isFirstStop: i === 0,
                      visibilityLevel,
                      routeColor: "#000000"
                    }
                  }
                ];
              });
            });
      const locations = await Promise.all(
        [...visibleManifestStops].map((stop) =>
          getStopLatLng(stop, geoCodeService).then((latLngPair) => {
            return {latLngPair, stop};
          })
        )
      );
      const latLngStops: Record<string, MappedStop[]> = {};
      for (const {latLngPair, stop} of locations) {
        /* skip addresses that fail to produce a lat/lng */
        if (latLngPair) {
          if (!latLngStops[latLngPair]) latLngStops[latLngPair] = [];
          latLngStops[latLngPair].push(stop);
        }
      }
      if (stillMounted) {
        /*
         * Compare the stop *values*, not just the "lat,lng" keys. Completing a stop keeps its
         * address, so the previous key-only comparison returned `prev` and froze the marker at
         * its pre-completion colour. NB: the local `latLngStops` above shadows the state, so the
         * functional form is the only way to reach the previous value.
         */
        setLatLngStops((prev) => (sameDrawnStops(prev, latLngStops) ? prev : latLngStops));
      }
    })();
    return () => {
      stillMounted = false;
    };
  }, [
    mapVisibilityState.manifests,
    geoCodeService,
    manifest,
    optimizedStops,
    map,
    maps,
    viewMode,
    isDraggedStops,
    isActiveCompletedStops,
    isOptimized
  ]);

  // Set Driver Locations
  useEffect(() => {
    if (!driverLocationResult.loading) {
      const newDriverLocations = new Map<number, DriverLocation>();
      const data = driverLocationResult.data?.searchDriverLocations?.items as DriverLocation[];
      if (data) {
        for (const driverLocation of data) {
          newDriverLocations.set(driverLocation.driverId, driverLocation);
        }
      } else {
        log.error("Unable to retrieve driver locations", driverLocationResult.error);
      }
      if (!mapCompare<number, DriverLocation>(driverLocations, newDriverLocations)) {
        setDriverLocations(newDriverLocations);
      }
    }
  }, [
    setDriverLocations,
    driverLocations,
    driverLocationResult.loading,
    driverLocationResult.data,
    driverLocationResult.error
  ]);

  useEffect(() => {
    // set driver id from manifest data
    if (manifest) {
      // set visible manifests to those that have a driver location
      const manifestsWithLocation = driverLocations.has(manifest.driver.driverId) ? [manifest] : [];
      mapVisibilityDispatch({
        type: "SetManifestsWithLocation",
        manifests: manifestsWithLocation
      });
    }
  }, [manifest, driverLocations, mapVisibilityDispatch]);

  // Set center & clear route on new manifest or job selection
  useEffect(() => {
    if (manifest && mapVisibilityState.manifests) {
      const routeMarkers = [
        ...(routeInfo ?? []).flatMap((x) =>
          x.points.map((y) => {
            return {lat: y[0], lng: y[1]};
          })
        )
      ];
      const stopMarkers = Object.keys(latLngStops).map((key) => {
        const [lat, lng] = key.split(",");
        return {lat: Number(lat), lng: Number(lng)};
      });

      const driverCords = [getDriverCords(manifest.driver.driverId, driverLocations)] as Cords[];
      const markers = [...routeMarkers, ...stopMarkers, ...driverCords].filter((d) => d);
      if (apiLoaded && markers.length > 0 && !userGestureActive) {
        const bounds = getMapBounds(maps, markers);
        map.fitBounds(bounds);
        const currentZoom = map.getZoom();
        if (currentZoom && currentZoom > 15) {
          map.setZoom(15);
        }
      }
    }
  }, [
    map,
    maps,
    apiLoaded,
    routeInfo,
    mapVisibilityState.manifests,
    latLngStops,
    manifest,
    driverLocations,
    userGestureActive
  ]);

  //Setting stop gps points for routing path
  useEffect(() => {
    if (isOptimized && !isDraggedStops) return;
    const routedEntities = getRouted(mapVisibilityState);
    if (map && routedEntities.manifests.length > 0) {
      // stop locations array, keyed by unique entity id (manifest or nested job id)
      for (const key of Object.keys(latLngStops)) {
        const mappedStops = latLngStops[key];
        if (mappedStops) {
          for (const mappedStop of mappedStops) {
            const entityId = mappedStop.stopInfoMetaData.key;
            const x = map?.get(entityId) ?? [];
            x.push(mappedStop);
            map.set(entityId, x);
          }
        }
      }
      const paths = getRoutePaths(latLngStops, routedEntities, driverLocations, false);
      const lastRoutePath = paths.at(-1);
      const newPaths: RoutePath[] = lastRoutePath
        ? [
            {
              ...lastRoutePath,
              key: `${lastRoutePath.key}_${Object.keys(latLngStops).join("_")}`,
              mode: viewMode
            }
          ]
        : [];
      setRoutes(newPaths);
    } else {
      setRoutes([]);
    }
  }, [driverLocations, latLngStops, mapVisibilityState, map, setRoutes, viewMode, isOptimized, isDraggedStops]);

  useEffect(() => {
    if (isOptimized && !isDraggedStops) return;
    if (routeInfo && routeInfo.length > 0) {
      const lastRouteInfo = routeInfo.filter((route) => route.mode === viewMode).at(-1);
      if (lastRouteInfo) {
        const mapPath: MapPath = {
          route: lastRouteInfo,
          polyline: createSinglePolyline(
            lastRouteInfo,
            map,
            viewMode === RouteViewMode.Optimized ? "#003DB5" : "#797979"
          )
        };
        setRoutePathLine((mapPathPrev) => {
          removePolyLines(mapPathPrev?.polyline);
          return mapPath;
        });
      }
    }
    if (Object.keys(latLngStops).length === 0) {
      setRoutePathLine((mapPathPrev) => {
        removePolyLines(mapPathPrev?.polyline);
        return null;
      });
    }
  }, [map, routeInfo, viewMode, latLngStops, isOptimized, isDraggedStops]);

  useEffect(() => {
    if (!isOptimized || !optimizedResultCords || isDraggedStops) return;

    if (viewMode === RouteViewMode.Optimized) {
      const optimizedCords = optimizedResultCords.get(OptimizedResultCords.OptimizedCords);
      const newPath: MapPath = {
        polyline: new google.maps.Polyline({
          strokeColor: "#003DB5",
          strokeOpacity: 1,
          strokeWeight: 4,
          path: optimizedCords,
          map: map
        })
      };
      setRoutePathLine((mapPathPrev) => {
        removePolyLines(mapPathPrev?.polyline);
        return newPath;
      });
    } else {
      const originalCords = optimizedResultCords.get(OptimizedResultCords.OriginalCords);
      const newPath: MapPath = {
        polyline: new google.maps.Polyline({
          strokeColor: "#797979",
          strokeOpacity: 1,
          strokeWeight: 4,
          map: map,
          path: originalCords
        })
      };
      setRoutePathLine((mapPathPrev) => {
        removePolyLines(mapPathPrev?.polyline);
        return newPath;
      });
    }
  }, [isDraggedStops, isOptimized, map, optimizedResultCords, viewMode]);

  const removePolyLines = (lines: google.maps.Polyline | google.maps.Polyline[] | undefined) => {
    if (lines !== undefined) {
      if (_.isArray(lines)) {
        lines.forEach((polyline) => {
          polyline.setMap(null);
        });
      } else {
        lines.setMap(null);
      }
    }
  };

  /* event from GoogleMapReact when map is loading in browser */
  const apiIsLoaded = useCallback((maps: {map: any; maps: any; ref: Element}) => {
    setMap(maps.map);
    setMaps(maps.maps);
    setApiLoaded(true);
  }, []);

  const [driverContextMenu, setDriverContextMenu] = useState<{x: number; y: number; lat: number; lng: number} | null>(
    null
  );

  const handleDriverContextMenu = useCallback((e: React.MouseEvent, lat: number, lng: number) => {
    const MENU_WIDTH = 160;
    const MENU_HEIGHT = 40;
    const x = Math.min(e.clientX, window.innerWidth - MENU_WIDTH - 8);
    const y = Math.min(e.clientY, window.innerHeight - MENU_HEIGHT - 8);
    setDriverContextMenu({x, y, lat, lng});
  }, []);

  useEffect(() => {
    if (!driverContextMenu) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDriverContextMenu(null);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [driverContextMenu]);

  const handleOpenGoogleMaps = useCallback(() => {
    if (driverContextMenu) {
      window.open(
        `https://www.google.com/maps?q=${driverContextMenu.lat},${driverContextMenu.lng}`,
        "_blank",
        "noopener,noreferrer"
      );
      setDriverContextMenu(null);
    }
  }, [driverContextMenu]);

  const renderManifestDriver = () => {
    if (manifest) {
      const cords = getDriverCords(manifest.driver.driverId, driverLocations);
      return cords ? (
        <DriverMarker
          manifest={manifest}
          key={`manifest-driver-marker-${manifest.manifestDriverId}`}
          isSelected={true}
          lat={cords.lat}
          lng={cords.lng}
          cords={cords}
          onContextMenu={handleDriverContextMenu}
        />
      ) : null;
    }
    return null;
  };

  const isShowOptmizedInfo = useMemo(() => {
    if (!routeInfo || !optimizedInfo || (isOptimized && isDraggedStops)) return false;
    return true;
  }, [isDraggedStops, isOptimized, routeInfo, optimizedInfo]);

  const renderHomeLocation = useCallback(() => {
    if (
      !isOptimized ||
      !optimizedResultCords ||
      !optimizedResultCords.has(OptimizedResultCords.HomeCords) ||
      isDraggedStops
    ) {
      return null;
    }

    const homeCords = optimizedResultCords.get(OptimizedResultCords.HomeCords)?.at(0);
    return (
      <IconHomeContainer lat={homeCords?.lat ?? 0} lng={homeCords?.lng ?? 0}>
        <Icon icon={"home"} size={20} color={"red"} />
      </IconHomeContainer>
    );
  }, [isDraggedStops, isOptimized, optimizedResultCords]);

  return (
    <div
      className={`${map ? "google-map" : ""}`}
      data-testid={"manifest-details-map"}
      style={{height: "100%", position: "relative"}}
      onWheel={handleUserGestureActive}
    >
      <ManifestDetailActionBar
        isOptimized={isOptimized}
        isActiveCompletedStops={isActiveCompletedStops}
        onToggleActiveCompletedStops={onToggleActiveCompletedStops}
      />
      <ManifestDetailsMapOverlay
        isOptimized={isOptimized}
        viewMode={viewMode}
        setViewMode={setViewMode}
        isShowOptmizedInfo={isShowOptmizedInfo}
        optimizedInfo={optimizedInfo}
      />
      <GoogleMapReact
        bootstrapURLKeys={apiKey}
        defaultCenter={defaultCenter}
        defaultZoom={5}
        yesIWantToUseGoogleMapApiInternals
        options={{disableDefaultUI: true, zoomControl: true, clickableIcons: true}}
        onGoogleApiLoaded={apiIsLoaded}
        onDragEnd={handleUserGestureActive}
        onChildClick={handleUserGestureActive}
      >
        {renderManifestDriver()}
        {renderHomeLocation()}
        {Object.keys(latLngStops).length !== 0 &&
          Object.keys(latLngStops).map((key) => {
            const mappedLocations = latLngStops[key];
            const [lat, lng] = key.split(",");
            return (
              <StopMarker
                mapJobStop={mapJobStop}
                key={key}
                lat={Number(lat)}
                lng={Number(lng)}
                mappedStops={mappedLocations}
              />
            );
          })}
      </GoogleMapReact>
      {driverContextMenu && (
        <DriverContextMenuOverlay onClick={() => setDriverContextMenu(null)}>
          <DriverContextMenu style={{top: driverContextMenu.y, left: driverContextMenu.x}}>
            <DriverContextMenuItem onClick={handleOpenGoogleMaps}>View on Google Maps</DriverContextMenuItem>
          </DriverContextMenu>
        </DriverContextMenuOverlay>
      )}
    </div>
  );
};
export default ManifestDetailsMap;

const DriverContextMenuOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
`;

const DriverContextMenu = styled.div`
  position: fixed;
  background: #ffffff;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 160px;
  z-index: 2001;
`;

const DriverContextMenuItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #161616;

  &:hover {
    background: #f0f0f0;
  }
`;

const IconHomeContainer = styled.div<{lat: number; lng: number}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #ffffff;
`;
